import {
  BlockOutlined,
  BulbOutlined,
  HeartOutlined,
  MessageOutlined,
  SettingOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { USER_ROLES, canAccessAdmin } from '@cult/domain';
import { Link, type LinkProps, createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import type { ComponentType } from 'react';
import { ShortcutSection, SupportWidget } from '../../components';
import { useCurrentUser } from '../../state/use-current-user';
import styles from './home.module.scss';

type Shortcut = {
  name: string;
  icon: ComponentType;
  iconClassName: string | undefined;
} & ({ to: LinkProps['to'] } | { href: string }); // `to` = in-app route, `href` = external page

const aboutApps: Shortcut[] = [
  {
    name: 'Who We Are',
    href: 'https://docs.google.com/document/d/1ZWrxLiZ-hfpsuVngCHgg9EGlcY1rh-o-JcWcGwT97AE/edit?tab=t.nx7ts2q90nuo#heading=h.nwvm9tmfi2c',
    icon: TeamOutlined,
    iconClassName: styles.whoWeAre,
  },
  {
    name: 'Core Values',
    href: 'https://docs.google.com/document/d/1ZWrxLiZ-hfpsuVngCHgg9EGlcY1rh-o-JcWcGwT97AE/edit?tab=t.vcfbxusjmvg#heading=h.7zf3cwjt75dw',
    icon: HeartOutlined,
    iconClassName: styles.coreValues,
  },
  {
    name: 'Frameworks',
    href: 'https://docs.google.com/document/d/1-JTogUgcF_2HxTbP_mYOHNKp-HVP44vPxSYGZdueBgE/edit?pli=1&tab=t.pgy8k4vck7iu',
    icon: BlockOutlined,
    iconClassName: styles.frameworks,
  },
];

const workspaceApps: Shortcut[] = [
  {
    name: 'Ideas',
    to: '/ideas',
    icon: BulbOutlined,
    iconClassName: styles.ideas,
  },
  {
    name: 'Praises',
    to: '/praises',
    icon: TrophyOutlined,
    iconClassName: styles.praises,
  },
  {
    name: 'Direct Feedback',
    to: '/feedback',
    icon: MessageOutlined,
    iconClassName: styles.feedback,
  },
];

const adminApps: Shortcut[] = [
  {
    name: 'Administration',
    to: '/admin',
    icon: SettingOutlined,
    iconClassName: styles.administration,
  },
];

const ShortcutTile = (shortcut: Shortcut) => {
  const { name, icon: Icon, iconClassName } = shortcut;
  const content = (
    <>
      <span className={`${styles.appIcon} ${iconClassName}`} aria-hidden="true">
        <Icon />
      </span>
      <span className={styles.appName}>{name}</span>
    </>
  );

  if ('href' in shortcut) {
    return (
      <a href={shortcut.href} target="_blank" rel="noopener noreferrer" className={styles.appCard}>
        {content}
      </a>
    );
  }

  return (
    <Link to={shortcut.to} className={styles.appCard}>
      {content}
    </Link>
  );
};

const HomePage = () => {
  const user = useCurrentUser((state) => state.user);
  const apps = user && canAccessAdmin(user.role) ? [...workspaceApps, ...adminApps] : workspaceApps;

  return (
    <main className={styles.page}>
      <div className={styles.ambientOne} aria-hidden="true" />
      <div className={styles.ambientTwo} aria-hidden="true" />

      <section className={styles.workspace} aria-labelledby="workspace-title">
        <header className={styles.heading}>
          <span className={styles.eyebrow}>YOUR WORKSPACE</span>
          <h1 id="workspace-title">Welcome to CULT</h1>
          <time className={styles.today} dateTime={dayjs().format('YYYY-MM-DD')}>
            {dayjs().format('dddd, D MMM YYYY')}
          </time>
        </header>

        <ShortcutSection title="Get to know us">
          {aboutApps.map((app) => (
            <ShortcutTile key={app.name} {...app} />
          ))}
        </ShortcutSection>

        <ShortcutSection title="What would you like to do today?">
          {apps.map((app) => (
            <ShortcutTile key={app.name} {...app} />
          ))}
        </ShortcutSection>
      </section>

      <SupportWidget showTechSupport={user !== null && user.role !== USER_ROLES.MEMBER} />
    </main>
  );
};

export const Route = createFileRoute('/_auth/home')({
  component: HomePage,
});
